// src/pages/Login.js
import React, { useMemo, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Link,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useAuth } from "../providers/AuthProvider";
import ResetPassword from "./ResetPassword";

const FEATURES = [
  "Find The Right Companies & Turn Connections Into Opportunities",
  "Support Business Retention & Expansion While Driving New Investment",
  "Track Every Lead, Project & Investment In One Place",
  "Build Outreach Lists & Drive Real Economic Impact",
];

const inputSx = (focusColor = "#648315") => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#dde3ec", borderWidth: 1.5 },
    "&:hover fieldset": { borderColor: "#b0bec5" },
    "&.Mui-focused fieldset": { borderColor: focusColor, borderWidth: 2 },
  },
  "& .MuiInputBase-input": {
    color: "#00336C",
    // Override browser autofill background
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0px 1000px #fff inset",
      WebkitTextFillColor: "#00336C",
    },
    "&:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0px 1000px #fff inset",
    },
    "&:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0px 1000px #fff inset",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: focusColor },
});

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const isAdminLoginPage = location.pathname === "/admin-login";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loadingMode, setLoadingMode] = useState(null);
  const [error, setError] = useState(null);

  const resetToken = useMemo(
    () => new URLSearchParams(location.search).get("reset_token"),
    [location.search],
  );

  if (resetToken) return <ResetPassword tokenOverride={resetToken} />;

  async function handleLogin(mode = isAdminLoginPage ? "admin" : "standard") {
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    const adminLogin = mode === "admin";
    setLoadingMode(mode);
    setError(null);
    try {
      const me = await login(email, password, { adminLogin });
      sessionStorage.setItem("justLoggedIn", "1");
      if (adminLogin) {
        if (me?.is_admin !== true) {
          setError("This account does not have admin access.");
          return;
        }
        sessionStorage.setItem("beSession", "1");
        sessionStorage.removeItem("fromModulePicker");
        navigate("/admin/create-org", { replace: true });
        return;
      }
      sessionStorage.setItem("beSession", "1");
      sessionStorage.removeItem("fromModulePicker");
      navigate("/home", { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Invalid email or password.";
      setError(typeof msg === "string" ? msg : "Invalid email or password.");
    } finally {
      setLoadingMode(null);
    }
  }

  const onKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  const isLoading = Boolean(loadingMode);
  const title = isAdminLoginPage ? "ProspectEngage Admin" : "Login";
  const subtitle = isAdminLoginPage
    ? "Sign in with an admin-enabled account"
    : "Sign in to your account to continue";
  const submitMode = isAdminLoginPage ? "admin" : "standard";

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100vw",
        overflow: "hidden",
      }}
    >
      {/* ── Left: Branded panel ── */}
      <Box
        sx={{
          flex: "0 0 46%",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          px: 7,
          py: 6,
          background:
            "linear-gradient(155deg, #0d1c30 0%, #0a2240 28%, #0e3560 52%, #111f06 78%, #182808 100%)",
          position: "relative",
          overflow: "hidden",
          // Dot-grid texture overlay
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
            pointerEvents: "none",
          },
        }}
      >
        {/* Blue glow — top right */}
        <Box
          sx={{
            position: "absolute",
            top: -120,
            right: -120,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(25,80,160,0.12) 0%, transparent 65%)",
            pointerEvents: "none",
          }}
        />

        {/* Green glow — bottom left (dominant) */}
        <Box
          sx={{
            position: "absolute",
            bottom: -100,
            left: -100,
            width: 480,
            height: 480,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(70,90,20,0.18) 0%, transparent 60%)",
            pointerEvents: "none",
          }}
        />

        {/* Green glow — mid right */}
        <Box
          sx={{
            position: "absolute",
            top: "48%",
            right: -60,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(70,90,20,0.09) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        {/* Badge */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "rgba(100,131,21,0.18)",
            border: "1px solid rgba(100,131,21,0.45)",
            borderRadius: "20px",
            px: 1.75,
            py: 0.6,
            mb: 2.5,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: "#8fb528",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              color: "#a8c934",
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: 1.6,
              textTransform: "uppercase",
            }}
          >
            Economic Development CRM
          </Typography>
        </Box>

        <Typography
          variant="h3"
          sx={{
            color: "#fff",
            fontWeight: 800,
            lineHeight: 1.2,
            mb: 1.5,
            position: "relative",
            zIndex: 1,
            letterSpacing: -0.5,
            fontSize: "3rem",
          }}
        >
          Prospect Smarter.
          <br />
          Grow Faster.
        </Typography>

        {/* Green accent bar */}
        <Box
          sx={{
            width: 52,
            height: 3.5,
            background: "linear-gradient(90deg, #8fb528, rgba(100,131,21,0))",
            borderRadius: 2,
            mb: 2,
            position: "relative",
            zIndex: 1,
          }}
        />

        <Typography
          sx={{
            color: "rgba(255,255,255,0.55)",
            mb: 4.5,
            fontSize: "1.2rem",
            lineHeight: 1.65,
            position: "relative",
            zIndex: 1,
          }}
        >
          The All-In-One Platform Built For
          <br />
          Economic Development Professionals.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.25,
            position: "relative",
            zIndex: 1,
          }}
        >
          {FEATURES.map((text, i) => (
            <Box
              key={i}
              sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
            >
              <CheckCircleOutlineIcon
                sx={{ color: "#8fb528", fontSize: 20, mt: 0.3, flexShrink: 0 }}
              />
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.78)",
                  fontSize: "1.1rem",
                  lineHeight: 1.55,
                }}
              >
                {text}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            position: "absolute",
            bottom: 28,
            left: 56,
            zIndex: 1,
            color: "rgba(255,255,255,0.2)",
            fontSize: "0.7rem",
            letterSpacing: 1.2,
            textTransform: "uppercase",
          }}
        >
          © {new Date().getFullYear()} ProspectEngage
        </Typography>
      </Box>

      {/* ── Right: Login form ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          // Subtle green-to-neutral gradient background
          background:
            "linear-gradient(150deg, #e8f5d4 0%, #f2f8e8 18%, #f7faf3 40%, #f8fafc 100%)",
          px: { xs: 3, sm: 6 },
          py: 4,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Faint decorative circles in background */}
        <Box
          sx={{
            position: "absolute",
            top: -80,
            right: -80,
            width: 320,
            height: 320,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(100,131,21,0.07) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -60,
            left: -60,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(0,51,108,0.05) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <Typography
          sx={{
            color: "#00336C",
            fontWeight: 800,
            fontSize: "2.4rem",
            letterSpacing: -0.5,
            mb: 4,
            width: "100%",
            maxWidth: 760,
            textAlign: "left",
            display: { xs: "none", md: "block" },
          }}
        >
          Prospect<Box component="span" sx={{ color: "#648315" }}>Engage</Box>
        </Typography>

        {/* Form card */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 760,
            backgroundColor: "#fff",
            borderRadius: "16px",
            boxShadow:
              "0 4px 24px rgba(0,51,108,0.09), 0 1px 4px rgba(0,0,0,0.05)",
            p: { xs: 6, sm: 9 },
            position: "relative",
            overflow: "hidden",
            // Green-to-navy top accent bar
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #648315 0%, #00336C 100%)",
            },
          }}
        >
          {/* Mobile-only wordmark */}
          <Typography
            component="div"
            aria-label="ProspectEngage"
            sx={{
              display: { xs: "block", md: "none" },
              fontSize: 28,
              fontWeight: 900,
              lineHeight: 1,
              mb: 4,
            }}
          >
            <Box component="span" sx={{ color: "#00336C" }}>
              Prospect
            </Box>
            <Box component="span" sx={{ color: "#648315" }}>
              Engage
            </Box>
          </Typography>

          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.75 }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, color: "#00336C", letterSpacing: -0.4 }}
            >
              {title}
            </Typography>
            {/* Green accent dot */}
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#648315",
                mt: 0.25,
                flexShrink: 0,
              }}
            />
          </Box>

          <Typography sx={{ color: "#64748b", mb: 3.5, fontSize: "0.9rem" }}>
            {subtitle}
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2.5, borderRadius: "10px", fontSize: "0.875rem" }}
            >
              {error}
            </Alert>
          )}

          <TextField
            label="Email address"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="username"
            type="email"
            inputProps={{ inputMode: "email" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: "#94a3b8", fontSize: 19 }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, ...inputSx() }}
          />

          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: "#94a3b8", fontSize: 19 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{ fontSize: 18, color: "#94a3b8" }} />
                    ) : (
                      <Visibility sx={{ fontSize: 18, color: "#94a3b8" }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1, ...inputSx() }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            {isAdminLoginPage ? (
              <Link
                component="button"
                type="button"
                underline="none"
                disabled={isLoading}
                onClick={() => navigate("/login")}
                sx={{
                  color: "#648315",
                  fontWeight: 600,
                  fontSize: "0.83rem",
                  "&:hover": { color: "#4a6010" },
                  "&:disabled": {
                    color: "#94a3b8",
                    cursor: "default",
                  },
                }}
              >
                Standard Login
              </Link>
            ) : (
              <Link
                component="button"
                type="button"
                underline="none"
                disabled={isLoading}
                onClick={() => navigate("/admin-login")}
                sx={{
                  color: "#648315",
                  fontWeight: 600,
                  fontSize: "0.83rem",
                  "&:hover": { color: "#4a6010" },
                  "&:disabled": {
                    color: "#94a3b8",
                    cursor: "default",
                  },
                }}
              >
                Admin Login
              </Link>
            )}
            <Link
              component="button"
              type="button"
              underline="none"
              disabled={isLoading}
              onClick={() => navigate("/forgot-password")}
              sx={{
                color: "#648315",
                fontWeight: 600,
                fontSize: "0.83rem",
                "&:hover": { color: "#4a6010" },
                "&:disabled": {
                  color: "#94a3b8",
                  cursor: "default",
                },
              }}
            >
              Forgot/Reset Password
            </Link>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={() => handleLogin(submitMode)}
            disabled={isLoading}
            sx={{
              py: 1.5,
              borderRadius: "10px",
              backgroundColor: "#00336C",
              fontWeight: 700,
              fontSize: "0.975rem",
              textTransform: "none",
              letterSpacing: 0.3,
              boxShadow: "0 2px 8px rgba(0,51,108,0.25)",
              "&:hover": {
                backgroundColor: "#002a58",
                boxShadow: "0 4px 16px rgba(0,51,108,0.35)",
              },
              "&.Mui-disabled": {
                background: "#e2e8f0",
                color: "#94a3b8",
                boxShadow: "none",
              },
            }}
          >
            {loadingMode === submitMode ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Sign in"
            )}
          </Button>
          <Box
            component="nav"
            aria-label="Legal and privacy"
            sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 3, mt: 3 }}
          >
            <Link component={RouterLink} to="/legal" sx={{ color: "#00336C", fontSize: "0.875rem" }}>
              Legal
            </Link>
            <Link component={RouterLink} to="/privacy" sx={{ color: "#00336C", fontSize: "0.875rem" }}>
              Privacy Policy
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
